import type { ApprovalDetail, ShareFraction } from '@myrewards/contracts';
import { useState, type ReactElement } from 'react';
import { Label, RadioButton, RadioField, RadioGroup } from 'react-aria-components';
import { saveReview } from '../../api/ops';
import { Button, ConfirmDialog, plural } from '../../ui';
import { CountInput, Notice } from '../shared';
import { ChangeStep, type ActingRefusal } from './acting';
import {
  actingOffer,
  reviewPayload,
  sameShare,
  sharePercent,
  SPLIT_PRESETS,
  splitWords,
  taaPools,
} from './presenter';
import { useApprovalCommand } from './useApprovalCommand';
import s from '../Ops.module.css';

interface SplitOption {
  key: string;
  /** Null for Custom, whose share is typed in. */
  share: ShareFraction | null;
  label: string;
}

/**
 * QA-048 (APP-070 D15): an Executive changes a TAA's split for this award, by person. The presets
 * are the X3 board's (the PAA/TAA pool's part first), plus the award's own default when it isn't
 * one of them, and Custom. A change needs a reason; the server re-runs the assignment (each person
 * wholly on one pool, as close to the split as whole people allow) and logs it. Choosing the default
 * clears the change. Moving one person by hand stays for the exception.
 */
export function TaaSplitControl({
  detail,
  isDisabled,
  onChanged,
  onRefused,
}: {
  detail: ApprovalDetail;
  /** Unsaved amount edits: save or undo them first, so the split runs on what was saved. */
  isDisabled: boolean;
  onChanged: (before: ApprovalDetail, after: ApprovalDetail, share: ShareFraction) => void;
  /** A change for an Executive refused because the page moved on: the page says why. */
  onRefused?: ((refusal: ActingRefusal) => void) | undefined;
}): ReactElement | null {
  const funding = detail.taaFunding;
  const pools = taaPools(detail);
  // APP-148: a Stock Admin changes the split only for a named Executive: the step, not the
  // confirm, with the same reason. It keeps the offer it opened with.
  const offer = actingOffer(detail);
  const forExecutive =
    !detail.actions.changeTaaSplit && offer?.role === 'executive' && offer.actions.changeTaaSplit
      ? offer
      : null;
  const [choice, setChoice] = useState<string | null>(null);
  const [custom, setCustom] = useState<number | null>(null);
  const [pending, setPending] = useState<ShareFraction | null>(null);
  const [opened, setOpened] = useState<typeof forExecutive>(null);
  const [customProblem, setCustomProblem] = useState<string | null>(null);
  const save = useApprovalCommand(
    detail.award.id,
    (vars: { share: ShareFraction; reason: string }) =>
      saveReview(detail.award.id, {
        ...reviewPayload(detail, {}),
        // Back to the default clears this award's change, so the record reads as the default.
        taaShareOverride:
          funding != null && sameShare(vars.share, funding.defaultShare) ? null : vars.share,
        reason: vars.reason,
      }),
    (next, vars) => {
      setChoice(null);
      setPending(null);
      onChanged(detail, next, vars.share);
    },
  );
  if (funding == null || pools === null) return null;

  const presets = SPLIT_PRESETS.map((p) => ({ num: p, den: 100 }));
  const options: SplitOption[] = [
    ...(presets.some((p) => sameShare(p, funding.defaultShare))
      ? []
      : [
          {
            key: 'default',
            share: funding.defaultShare,
            label: `${splitWords(funding.defaultShare)} · default`,
          },
        ]),
    ...presets.map((share) => ({
      key: `p${String(share.num)}`,
      share,
      label: `${splitWords(share)}${sameShare(share, funding.defaultShare) ? ' · default' : ''}`,
    })),
    { key: 'custom', share: null, label: 'Custom' },
  ];
  const current =
    options.find((o) => o.share !== null && sameShare(o.share, funding.paaShare))?.key ?? 'custom';
  const selected = choice ?? current;
  const words = (share: ShareFraction): string => {
    const paa = sharePercent(share);
    return `${String(paa)}% from the ${pools.paa.name} pool, ${String(100 - paa)}% from the ${pools.sector.name} pool`;
  };
  const people = detail.lines.filter((l) => l.included).length;
  return (
    <div className={s.splitControl}>
      <RadioGroup
        value={selected}
        isDisabled={isDisabled || save.isPending}
        orientation="horizontal"
        className={s.splitGroup}
        onChange={(key) => {
          setCustomProblem(null);
          save.clearError();
          if (key === 'custom') {
            setChoice('custom');
            setCustom(current === 'custom' ? sharePercent(funding.paaShare) : null);
            return;
          }
          const option = options.find((o) => o.key === key);
          if (option?.share == null || sameShare(option.share, funding.paaShare)) {
            setChoice(null);
            return;
          }
          setChoice(key);
          setPending(option.share);
          setOpened(forExecutive);
        }}
      >
        <Label className={s.fieldName}>Split for this award · by person</Label>
        <div className={s.segmented}>
          {options.map((o) => (
            <RadioField
              key={o.key}
              value={o.key}
              className={s.splitField}
              aria-label={o.share === null ? 'Custom split' : `${o.label}: ${words(o.share)}`}
            >
              <RadioButton className={s.splitOption}>{o.label}</RadioButton>
            </RadioField>
          ))}
        </div>
      </RadioGroup>
      {selected === 'custom' && (
        <div className={s.splitCustom}>
          <div className={s.inlineField}>
            <span className={s.fieldName}>From the {pools.paa.name} pool, in percent</span>
            <CountInput
              label={`Percent of the cash from the ${pools.paa.name} pool`}
              value={custom}
              isDisabled={isDisabled || save.isPending}
              onChange={(n) => {
                setCustomProblem(null);
                setCustom(n);
              }}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            isDisabled={isDisabled || save.isPending}
            onPress={() => {
              if (custom === null || custom < 0 || custom > 100) {
                setCustomProblem('Enter a whole percent from 0 to 100.');
                return;
              }
              const share = { num: custom, den: 100 };
              if (sameShare(share, funding.paaShare)) {
                setCustomProblem(`This award is already ${splitWords(share)} by person.`);
                return;
              }
              setPending(share);
              setOpened(forExecutive);
            }}
          >
            Review the change
          </Button>
        </div>
      )}
      <p className={s.muted}>
        {pools.paa.name} pool first, then the {pools.sector.name} pool. The app puts each person
        wholly on one pool, as close to the split as whole people allow.
      </p>
      {customProblem !== null && <Notice tone="problem">{customProblem}</Notice>}
      {isDisabled && (
        <Notice tone="waiting">Save or undo your changes first, then change the split.</Notice>
      )}
      {pending !== null && opened !== null && (
        <ChangeStep
          detail={detail}
          open={{ kind: 'split', share: pending, offer: opened }}
          onRefused={onRefused}
          onChanged={(next) => {
            const share = pending;
            setChoice(null);
            setPending(null);
            setOpened(null);
            onChanged(detail, next, share);
          }}
          onClose={() => {
            setPending(null);
            setOpened(null);
            if (choice !== 'custom') setChoice(null);
          }}
        />
      )}
      <ConfirmDialog
        isOpen={pending !== null && opened === null}
        onOpenChange={(open) => {
          if (open) return;
          setPending(null);
          if (choice !== 'custom') setChoice(null);
          save.clearError();
        }}
        title={`Change the split to ${pending === null ? '' : splitWords(pending)} by person?`}
        intro={`The app moves people between the ${pools.paa.name} pool and the ${pools.sector.name} pool, each wholly to one, to come as close to the new split as whole people allow. Charge codes follow each person’s pool, and the change is logged with your reason.`}
        summary={[
          {
            label: 'Award',
            value: `${detail.award.displayId ?? 'This award'} · ${plural(people, 'person', 'people')}`,
          },
          {
            label: 'Split · by person',
            before: splitWords(funding.paaShare),
            value: pending === null ? '' : `${splitWords(pending)} · ${words(pending)}`,
          },
          {
            label: 'This award’s default',
            value: `${splitWords(funding.defaultShare)} · the split it was sent with`,
          },
        ]}
        reasonLabel="Why change the split?"
        confirmLabel="Change the split"
        error={save.error}
        onConfirm={(reason) =>
          pending === null
            ? false
            : // A refusal keeps the dialog open with the reason it gives.
              save.mutateAsync({ share: pending, reason }).then(
                () => true,
                () => false,
              )
        }
      />
    </div>
  );
}
